import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LuKeyRound, LuArrowLeft } from "react-icons/lu";
import api from "../../interceptor/Interceptor";
import { VERIFY_PASSWORD_TOKEN, SET_PASSWORD_WITH_TOKEN } from "../../api/ApiEndpoints";
import FloatingPasswordInput from "../../common/FloatingPasswordInput";
import Button from "../../common/Button";
import Loader from "../../common/Loader";
import Toaster, { notify } from "../../common/Toast";
import { containerVariants, itemVariants } from "../../common/MotionVariants";
import { LOGIN } from "../../routes/RouteConstant";

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const email = searchParams.get("user") || "";

  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [invalidReason, setInvalidReason] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      setInvalidReason("This link is incomplete. Please use the link from your email.");
      setStatus("invalid");
      return;
    }
    api
      .post(VERIFY_PASSWORD_TOKEN, { email, token }, { skipAuthRedirect: true })
      .then(() => setStatus("valid"))
      .catch((error) => {
        setInvalidReason(
          extractErrorMessage(error, "This link is invalid or has expired."),
        );
        setStatus("invalid");
      });
  }, [token, email]);

  const passwordError =
    touched && (password.length < 8 || password.length > 32 || !PASSWORD_REGEX.test(password))
      ? "Password must be 8-32 characters and include upper & lower case letters, a number, and a special character (@$!%*?&)."
      : "";
  const confirmError =
    touched && confirm !== password ? "Passwords do not match." : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (
      password.length < 8 ||
      password.length > 32 ||
      !PASSWORD_REGEX.test(password) ||
      confirm !== password
    ) {
      return;
    }
    setLoading(true);
    try {
      await api.post(
        SET_PASSWORD_WITH_TOKEN,
        { email, token, newPassword: password },
        { skipAuthRedirect: true },
      );
      notify.success("Password set", "You can now log in with your new password.");
      setTimeout(() => navigate(LOGIN, { replace: true }), 1500);
    } catch (error) {
      notify.error("Failed to set password", extractErrorMessage(error, "Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-[#0A0F1A] px-6 py-12 font-['IBM_Plex_Sans']">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 50% at 15% 10%, rgba(54,82,255,0.16), transparent 60%), radial-gradient(50% 40% at 85% 80%, rgba(109,58,235,0.12), transparent 60%)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="mb-8 flex items-center justify-center gap-2.5">
          <img
            src="https://mediafiles.projects.oceanicstudio.net/inads-cms-staging/ksas/logo_image/1709961414_logo-wht.png"
            alt="KSAS"
            className="h-9 w-auto"
          />
          <span className="h-4 w-px bg-white/15" />
          <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Lab Inventory
          </span>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-7 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] backdrop-blur-sm sm:p-9">
          <div className="mb-6 flex w-full items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(LOGIN)}
              className="group flex shrink-0 items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[11px] font-medium text-slate-400 transition-all duration-200 hover:border-[#5B7CFF]/30 hover:bg-[#5B7CFF]/10 hover:text-[#AAB8FF]"
            >
              <LuArrowLeft size={13} />
              <span>Back to login</span>
            </button>
            <p className="min-w-0 truncate font-['IBM_Plex_Mono'] text-[10px] font-medium uppercase tracking-[0.18em] text-[#5B7CFF] sm:text-[11px]">
              KSAS <span className="mx-1 text-white/30">·</span> Set Password
            </p>
          </div>

          <div className="mb-7">
            <h1 className="text-2xl font-semibold tracking-tight text-white">
              Set your password
            </h1>
            {email && (
              <p className="mt-1.5 break-all text-sm text-slate-400">{email}</p>
            )}
          </div>

          {status === "checking" && <Loader text="Verifying link..." />}

          {status === "invalid" && (
            <div className="rounded-md border border-rose-500/20 bg-rose-500/10 px-3.5 py-2.5 text-sm font-medium text-rose-300">
              {invalidReason} Ask your admin to send a new set-password email.
            </div>
          )}

          {status === "valid" && (
            <motion.form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
              noValidate
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              <motion.div variants={itemVariants}>
                <FloatingPasswordInput
                  id="new-password"
                  label="New password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={passwordError}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <FloatingPasswordInput
                  id="confirm-password"
                  label="Confirm password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  error={confirmError}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <Button type="submit" icon={LuKeyRound} variant="accent" loading={loading}>
                  Set password
                </Button>
              </motion.div>
            </motion.form>
          )}
        </div>
      </motion.div>

      <Toaster />
    </div>
  );
}
